import type {FC} from 'react';
import {useNavigate} from 'react-router-dom';
// web has no ButtonBase wrapper; it backs the "View all signals" link
import {ButtonBase} from '@mui/material';
import {useQuery} from '@tanstack/react-query';
import {Box, Button, Icon, Typography} from '@wellstone-solutions/web';
import {LD_FLAGS, useFeatureFlag} from 'modules/feature-flags';
import {ALERT_TYPES, showAlert} from 'utils/showAlert';
import {pluralize} from 'utils/Utils';
import {feedbackFormQueryOptions, kindsQueryOptions} from '../../queries';
import {formatRelativeTime} from '../../relativeTime';
import {useMemberSignals} from '../../useMemberSignals';
import {useSignalFeedback} from '../../useSignalFeedback';
import {FeedbackModal} from '../FeedbackModal';
import {SignalCard} from '../SignalCard';

type MemberSignalsCardProps = {
  // Member id (route param `member`), not the auth-user id
  memberId: string;
  memberName: string;
};

const latestComputedAt = (values: string[]): string =>
  values.reduce(
    (latest, value) =>
      Date.parse(value) > Date.parse(latest) ? value : latest,
    values[0] ?? '',
  );

// The member's newly-raised (unreviewed) signals, surfaced below the header card
// on Member Detail. Self-fetching (mirrors RecoveryCounter): give it a member id
// and it loads its own data, feedback form and kind labels.
//
// States:
//  - loading / error: renders nothing
//  - member has signals but none unreviewed: renders nothing (this viewer has
//    reviewed them all; other staff still see their own card)
//  - member has no signals at all: an informational empty container
//  - unreviewed signals present: the "new signals" card
export const MemberSignalsCard: FC<MemberSignalsCardProps> = ({
  memberId,
  memberName,
}) => {
  const navigate = useNavigate();
  const canCreateNote = useFeatureFlag(LD_FLAGS.CONTACT_NOTES);
  const {indicators, unreviewed, unreviewedCount, isPending, isError} =
    useMemberSignals(memberId);
  const formQuery = useQuery(feedbackFormQueryOptions());
  const kindsQuery = useQuery(kindsQueryOptions());
  const feedback = useSignalFeedback();

  const firstName = memberName.trim().split(/\s+/)[0] || memberName;

  if (isPending || isError) {
    return null;
  }

  // Had signals, nothing left to review for this viewer — collapse away.
  if (indicators.length > 0 && unreviewedCount === 0) {
    return null;
  }

  if (indicators.length === 0) {
    return (
      <Box sx={styles.emptyCard} data-testid="member-signals-empty">
        <Typography sx={styles.emptyTitle}>
          No signals for {firstName} yet
        </Typography>
        <Typography sx={styles.emptySubtitle}>
          You'll see a signal here when something shifts in {firstName}'s
          activity.
        </Typography>
      </Box>
    );
  }

  const kindLabels = new Map(
    (kindsQuery.data ?? []).map((kind) => [kind.slug, kind.label]),
  );
  // `null` (404) means no form is deployed, so the feedback buttons stay hidden.
  const feedbackForm = formQuery.data ?? null;
  const raisedAt = latestComputedAt(unreviewed.map((i) => i.computedAt));

  // Look up the active signal in the full set, not `unreviewed`: submitting
  // feedback optimistically marks it reviewed, which would drop it from
  // `unreviewed` and unmount the modal mid-request (losing the entered answers
  // on failure). It stays open until the mutation succeeds or the user cancels.
  const active = feedback.feedbackSignalId
    ? indicators.find((indicator) => indicator.id === feedback.feedbackSignalId)
    : undefined;

  return (
    <>
      <Box sx={styles.card} data-testid="member-signals-card">
        <Box sx={styles.header}>
          <Icon name="AntennaSignal" size={20} color={colors.accent} />
          <Typography sx={styles.headerTitle}>
            {unreviewedCount} new {pluralize('signal', unreviewedCount)}
          </Typography>
          <Typography sx={styles.headerMeta}>
            Raised {formatRelativeTime(raisedAt)}
          </Typography>
          <Box sx={{flex: 1}} />
          {canCreateNote && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Icon name="Notes" size={18} />}
              onClick={() =>
                navigate(`/documentation/notes/new?patient=${memberId}`)
              }
              sx={styles.createNote}>
              Create Note
            </Button>
          )}
        </Box>

        <Box sx={styles.body}>
          {unreviewed.map((indicator) => (
            <SignalCard
              key={indicator.id}
              indicator={indicator}
              kindLabel={kindLabels.get(indicator.kind) ?? indicator.kind}
              onGiveFeedback={feedbackForm ? feedback.openFeedback : undefined}
            />
          ))}
        </Box>

        <Box sx={styles.footer}>
          <ButtonBase
            onClick={() => navigate(`/members/${memberId}/activity`)}
            sx={styles.viewAll}>
            View all signals
            <Icon name="NavArrowRight" size={16} />
          </ButtonBase>
        </Box>
      </Box>

      {active && feedbackForm && (
        <FeedbackModal
          form={feedbackForm}
          signalHeadline={active.headline}
          firstName={firstName}
          isSubmitting={feedback.submitFeedback.isPending}
          errorReason={feedback.feedbackError}
          onCancel={feedback.closeFeedback}
          onSubmit={(submission) =>
            feedback.submitFeedback.mutate(
              {indicatorId: active.id, submission},
              {
                onSuccess: () =>
                  showAlert(
                    `Thanks for the feedback on ${firstName}'s signal.`,
                    ALERT_TYPES.SUCCESS,
                  ),
              },
            )
          }
        />
      )}
    </>
  );
};

// Amber "new signals" theme, from the Figma design tokens for this card.
const colors = {
  accent: '#C25E00', // color/orange/38 — antenna icon
  title: '#8A4300', // color/orange/27 — "N new signals"
  meta: '#7A5634', // color/orange/34 — raised-at line
  headerBg: '#FFF6EC', // color/grey/96 4 — header bar
  headerBorder: '#F6DDBE', // color/orange/85 — header divider
  border: 'rgba(0, 0, 0, 0.12)',
};

const styles = {
  card: {
    mt: 1.5,
    width: '100%',
    bgcolor: 'background.paper',
    border: `1px solid ${colors.border}`,
    borderRadius: 2,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: 2,
    py: 1.25,
    bgcolor: colors.headerBg,
    borderBottom: `1px solid ${colors.headerBorder}`,
    color: colors.accent,
  },
  headerTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: colors.title,
  },
  headerMeta: {
    fontSize: 13,
    color: colors.meta,
  },
  // Outlined button reads on the amber header: white fill, neutral text/border
  createNote: {
    textTransform: 'none',
    whiteSpace: 'nowrap',
    bgcolor: 'background.paper',
  },
  body: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
    columnGap: 5,
    rowGap: 2,
    px: 2,
    py: 2,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    borderTop: '1px solid',
    borderColor: 'divider',
    px: 2,
    py: 1.5,
  },
  viewAll: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.25,
    fontSize: 14,
    fontWeight: 600,
    color: 'primary.main',
  },
  emptyCard: {
    mt: 1.5,
    width: '100%',
    bgcolor: '#FAFAFA',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 2,
    px: '19px',
    py: '17px',
  },
  emptyTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: 'text.primary',
  },
  emptySubtitle: {
    fontSize: 13,
    color: 'text.secondary',
    mt: 0.5,
  },
};
